<template>
    <layout>
        <template v-slot:header>
            <AppHead title="Mobile Tool Distributors" />
            <div class="admin-header-container align-items-start align-items-lg-center">
                <h2>Mobile Tool Distributors</h2>
                <jet-button class="btn-short ml-0 ml-md-2 mb-2 mb-md-0" :goesTo="route('mtd.create')"
                    icon="fas fa-plus">Create MTD</jet-button>
                <jet-button class="btn-short ml-0 ml-md-2 mb-2 mb-md-0"
                    :disable="syncProductsIsRunning || _syncProductsIsRunning" :title="syncProductsStatusText"
                    @click="runSyncProducts">
                    Sync Products
                </jet-button>
            </div>
            <BreadCrumbs activeItem="MTDs" :crumbs="{ home: 'Dashboard' }" />
            <flash-messages />
        </template>

        <template v-slot:default>
            <div class="row form">
                <div class="col-md-3 form-group">
                    <jet-label for="search" value="Search" />
                    <AutoComplete :suggestions="mtds" :delay="300" v-model="searchFilters.search_text"
                        @complete="searchMtds" placeholder="Search" class="w-100" />
                </div>

                <div v-if="!isRsm" class="col-md-3 form-group">
                    <jet-label for="rsm" value="RSM" />
                    <select id="rsm" class="form-control" v-model="searchFilters.rsm_id">
                        <option value="">Any</option>
                        <option v-for="rsm in this.rsms" :key="rsm.first_name" :value="rsm.id">
                            {{ rsm.first_name }} {{ rsm.last_name }}
                        </option>
                    </select>
                </div>

                <div class="col-md-3 form-group">
                    <jet-label for="not_ordered_since" value="Last Ordered Before:" />
                    <Calendar id="not_ordered_since" class="w-100" v-model="searchFilters.not_ordered_since"
                        :showIcon="true" dateFormat="mm/dd/yy" :selectOtherMonths="true" />

                    <div class="btn-group mt-1">
                        <jet-button class="btn-sm bg-white text-secondary"
                            @click="setDateByPeriod('not_ordered_since', 'week')">Week</jet-button>
                        <jet-button class="btn-sm bg-white text-secondary"
                            @click="setDateByPeriod('not_ordered_since', 'month')">Month</jet-button>
                        <jet-button class="btn-sm bg-white text-secondary"
                            @click="setDateByPeriod('not_ordered_since', 'year')">Year</jet-button>
                        <jet-button class="btn-sm bg-white text-secondary"
                            @click="setDateByPeriod('not_ordered_since', 'never')">Never Ordered</jet-button>
                    </div>
                </div>

                <div class="col-md-3 form-group">
                    <jet-label for="not_active_since" value="Last Active Before:" />
                    <Calendar id="not_active_since" class="w-100" v-model="searchFilters.not_active_since" :showIcon="true"
                        dateFormat="mm/dd/yy" :selectOtherMonths="true" />

                    <div class="btn-group mt-1">
                        <jet-button class="btn-sm bg-white text-secondary"
                            @click="setDateByPeriod('not_active_since', 'week')">Week</jet-button>
                        <jet-button class="btn-sm bg-white text-secondary"
                            @click="setDateByPeriod('not_active_since', 'month')">Month</jet-button>
                        <jet-button class="btn-sm bg-white text-secondary"
                            @click="setDateByPeriod('not_active_since', 'year')">Year</jet-button>
                        <jet-button class="btn-sm bg-white text-secondary"
                            @click="setDateByPeriod('not_active_since', 'never')">Never Active</jet-button>
                    </div>
                </div>

                <div class="col-md-3 form-group">
                    <jet-label for="tags" value="Tags:" />
                    <Multiselect v-model="searchFilters.tags" mode="tags" :options="tags" :searchable="true"
                        class="admin-multiselect" id="tags" />
                </div>
                <div class="col-md-3 form-group">
                    <jet-label for="exclude_tags" value="Exclude Tags:" />
                    <Multiselect v-model="searchFilters.exclude_tags" mode="tags" :options="tags" :searchable="true"
                        class="admin-multiselect-exclude" id="exclude_tags" />
                </div>
            </div>

            <div class="row">
                <div class="col-12">
                    <jet-button class="mr-1" btnStyle="dark" @click="clearSearch">Clear</jet-button>
                </div>
            </div>

            <DataTable ref="mtds-index" :value="users.data" :paginator="true" :lazy="true"
                :first="(users.current_page - 1) * usersPerPage" :rows="usersPerPage" :totalRecords="users.total"
                @page="onPage($event)" @sort="onSort($event)" @state-restore="onTableStateRestore($event)"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                removableSort paginatorPosition="both" currentPageReportTemplate="({first} - {last} of {totalRecords})"
                :rowsPerPageOptions="[10, 20, 50, 100, 500, 1000]" sortField="date" :sortOrder="-1"
                class="table p-datatable-sm admin-datatable" tableClass="border-left border-right border-bottom"
                @selection-change="onSelectionChange" v-model:selection="selectedUsers" stateStorage="local"
                stateKey="admin-mtd-index" dataKey="id">
                <Column class="text-center pl-2 pr-2" selectionMode="multiple"></Column>
                <Column field="id" header="#" class="text-center" :sortable="true"></Column>
                <Column field="status" header="Status" :sortable="true" sortField="mtd_status"></Column>
                <Column header="Name" field="name" :sortable="true" sortField="name"></Column>
                <Column header="Company" field="company_name" :sortable="true">
                    <template #body="slotProps">
                        <div class="text-nowrap">
                            {{ slotProps.data.company_name }} ({{ slotProps.data.state }}) ({{ slotProps.data.plan }})
                            <div v-if="slotProps.data.needsAttention">
                                <i class="fa fa-exclamation-circle text-secondary mr-2"></i> Needs Attention!
                            </div>
                        </div>
                        <div class="text-nowrap mt-2">
                            <jet-button class="btn-short m-1 text-nowrap m-1" :goesTo="route('mtd.view', slotProps.data.id)"
                                icon="fas fa-eye" title="View"></jet-button>
                            <jet-button class="btn-short text-nowrap m-1" :goesTo="route('mtd.edit', slotProps.data.id)"
                                icon="fas fa-edit" title="Edit"></jet-button>
                            <jet-button class="btn-short text-nowrap m-1" :goesTo="route('mtd.shopas', slotProps.data.id)"
                                icon="fas fa-sign-in-alt" title="Login As"></jet-button>
                            <jet-button class="btn-short text-nowrap m-1"
                                :goesTo="route('admin.mtd.shop', slotProps.data.id)" icon="fas fa-shopping-cart"
                                title="Checkout As"></jet-button>
                            <jet-button class="btn-short text-nowrap m-1"
                                :goesTo="route('admin.orders.usership', slotProps.data.id)" icon="fas fa-shipping-fast"
                                title="Ship Orders"></jet-button>
                            <jet-button class="btn-short text-nowrap m-1"
                                :goesTo="route('admin.returns.mtd', slotProps.data.id)" icon="fas fa-rotate-left"
                                title="Returns"></jet-button>
                        </div>
                    </template>
                </Column>
                <Column field="phone" header="Contact" :sortable="true" sortField="email">
                    <template #body="slotProps">
                        <div class="text-nowrap">
                            <Email v-model="slotProps.data.email" />
                        </div>
                        <div class="text-nowrap mt-2">
                            <Phone v-model="slotProps.data.phone" />
                        </div>
                    </template>
                </Column>
                <Column field="rsm" header="RSM" :sortable="true" v-if="!isRsm" sortField="rsm_name"></Column>
                <Column field="account_balance" header="Balance" class="text-right pr-3" :sortable="true" sortField="account_balance">
                    <template #body="slotProps">
                        {{ formatDollars(slotProps.data.account_balance) }}
                    </template>
                </Column>
                <Column field="created_at" header="Created" class="text-center" :sortable="true">
                    <template #body="slotProps">
                        {{ moment(slotProps.data.created_at).format('M/D/YY') }}
                    </template>
                </Column>
                <Column field="last_order_date" header="Last Order" class="text-center" :sortable="true">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.last_order_date">
                            {{ moment(slotProps.data.last_order_date).format('M/D/YY') }}
                        </span>
                        <span v-else>
                            No Orders
                        </span>
                    </template>
                </Column>
                <Column field="last_activity_at" header="Last Active" class="text-center" :sortable="true">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.last_activity_at">
                            {{ moment.utc(slotProps.data.last_activity_at).format('M/D/YY') }}
                        </span>
                        <span v-else>
                            No Activity
                        </span>
                    </template>
                </Column>
                <Column field="cart_value" header="Current Cart" class="text-right pr-3" :sortable="true" sortField="current_cart">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.cart_value">
                            {{ moment.utc(slotProps.data.cart_date).format('M/D/YY') }}  {{
                                formatDollars(slotProps.data.cart_value) }}
                        </span>
                        <span v-else>
                            No Cart
                        </span>
                    </template>
                </Column>
            </DataTable>

            <LocationMap ref="locationMap" :users="users.data" />

            <section class="my-3 row">
                <div class="col-12 col-md-6">
                    <card headerStyle="primary" icon="fas fa-share" title="Send Text" class="mb-3 mb-lg-0">
                        <div class="d-flex">
                            <textarea id="text_content" class="form-control mb-2" v-model="formText.content"></textarea>


                            <div class="d-flex mx-1 align-items-start">
                                <input id="attachTextMedia" accept="image/*" type="file" style="display:none;"
                                    @change="e => this.formText.media = e.target.files[0]" />
                                <label for="attachTextMedia" v-show="!formText.media" class="btn btn-light border px-0"
                                    style="width:110px">Attach Image</label>
                                <div class="d-flex" v-if="!!formText.media">
                                    <img :src="textMedia" style="max-width:75px;" />
                                    <button type="button" class="close" aria-label="Close"
                                        @click="this.formText.media = null;"><span aria-hidden="true">×</span></button>
                                </div>
                            </div>
                        </div>
                        <div class="d-flex">
                            <select class="form-control mb-2" v-model="formText.footer_message">
                                <option value="" disabled>Select footer message</option>
                                <option v-for="message in footerMessages" :value="message.id" :key="message.id">
                                    {{ message.message }}
                                </option>
                            </select>
                        </div>
                        <jet-button icon="fas fa-icon" @click="sendText()">Send to Selected Users</jet-button>
                        <card headerStyle="secondary" title="Send Text Status" class="my-3 border-0 text-histories">
                            <DataTable v-if="textHistories && textHistories.length > 0" :paginator="true" :rows="10"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                removableSort paginatorPosition="bottom"
                                currentPageReportTemplate="({first} - {last} of {totalRecords})"
                                :rowsPerPageOptions="[10, 20, 50, 100]" sortField="id" :sortOrder="-1"
                                class="p-datatable-sm admin-datatable" tableClass="border-left border-right border-bottom"
                                :value="textHistories" dataKey="id">
                                <Column header="Date" field="created_at" headerStyle="width: 10em">
                                    <template #body="slotProps">
                                        {{ moment(slotProps.data.created_at).format('M/D/YY H:m:s') }}
                                    </template>
                                </Column>
                                <Column header="Total" field="total"></Column>
                                <Column header="Processed" field="processed"></Column>
                                <Column header="Status">
                                    <template #body="slotProps">
                                        {{ slotProps.data.status }}
                                        <i class="fas fa-exclamation text-danger"
                                            v-if="slotProps.data.status === 'failed'"></i>
                                        <i class="fas fa-check text-success"
                                            v-if="slotProps.data.status === 'done' && !slotProps.data.errors"></i>
                                        <i class="fas fa-exclamation text-danger"
                                            v-if="slotProps.data.status === 'done' && !!slotProps.data.errors"></i>
                                        <a @click="showErrors(slotProps.data)" class="ml-2"
                                            v-if="!!slotProps.data.errors">Errors</a>
                                    </template>
                                </Column>
                            </DataTable>
                        </card>
                    </card>
                </div>

                <jet-dialog-modal id="textStatusErrorModal">
                    <template #title>
                        Text errors detail
                    </template>
                    <template #content>
                        <div v-if="textSendStatusError">
                            <table class="w-100 table small">
                                <tr>
                                    <th></th>
                                </tr>
                                <tr v-for="error in textSendStatusError.errors" :key="error">
                                    <td> {{ error }} </td>
                                </tr>
                            </table>
                        </div>
                    </template>
                </jet-dialog-modal>

                <div class="col-12 col-md-6">
                    <card headerStyle="primary" title="Bitly URL" class="mb-3">
                        <div class="d-flex align-items-center px-2 py-2 bg-light rounded border mb-2">
                            <input class="form-control custom-form-inline" type="text" v-model="url" />

                            <button class="mx-2 btn btn-secondary py-0" @click="getBitly">&gt;</button> <input
                                class="form-control custom-form-inline" type="text" v-model="bitly" />
                        </div>
                    </card>
                    <card headerStyle="primary" title="Test Number">
                        <input class="form-control mr-2" type="text" v-model="formText.test_number" />
                        <jet-button class="mt-2" icon="fas fa-mobile-alt" @click="sendTestText()">Send to Test
                            Number</jet-button>
                    </card>
                </div>
            </section>

            <hr>

            <section class="my-3 row">
                <div class="col-md-6">
                    <EditTags :tags="tags" :userIds="selectedUserIds"></EditTags>
                </div>
            </section>
        </template>
    </layout>
</template>
<style>
.text-histories .p-paginator {
    border: 0;
}
</style>

<script>
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Admin/Layout'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import FlashMessages from '@/Shared/FlashMessages'
import BreadCrumbs from '@/Shared/BreadCrumbs'
import JetButton from '@/Jetstream/Button'
import JetDialogModal from '@/Jetstream/DialogModal'
import JetLabel from '@/Jetstream/Label'
import JetInput from '@/Jetstream/Input'
import JetInputError from '@/Jetstream/InputError'
import moment from 'moment'
import Search from '@/Shared/Admin/Search'
import Calendar from 'primevue/calendar'
import Multiselect from '@vueform/multiselect'
import EditTags from "@/Shared/EditTags"
import LocationMap from '@/Components/General/LocationMap'
import AutoComplete from 'primevue/autocomplete'

export default {
    components: {
        EditTags,
        Icon,
        SearchFilter,
        Layout,
        FlashMessages,
        BreadCrumbs,
        JetButton,
        JetDialogModal,
        JetLabel,
        JetInput,
        JetInputError,
        Search,
        Calendar,
        Multiselect,
        LocationMap,
        AutoComplete
    },
    props: {
        users: Object,
        rsms: Array,
        isRsm: Boolean,
        filters: Object,
        tags: Array,
        syncProductsIsRunning: Boolean,
        syncProductsStatus: Object,
        textHistories: Array,
        mtdsLight: Array,
        footerMessages: Array,
    },
    data() {
        return {
            url: null,
            bitly: null,
            selectedUsers: [],
            formText: this.$inertia.form({
                _method: 'put',
                media: null,
                content: null,
                selected: null,
                test_number: null,
                footer_message: null,
            }),
            searchFilters: this.$inertia.form({
                search_text: '',
                rsm_id: null,
                tags: [],
                exclude_tags: [],
                not_ordered_since: '',
                not_active_since: '',
            }),
            _syncProductsIsRunning: false,
            textSendStatusError: null,
            checkHistoriesInterval: undefined,
            mtds: [],
        }
    },
    created() {
        this.moment = moment;
    },
    mounted() {
        this.checkHistoriesInterval = setInterval(() => {
            this.getHistories()
        }, 60000)
    },
    unmounted() {
        this.checkHistoriesInterval && clearInterval(this.checkHistoriesInterval)
    },
    watch: {
        users() {
            this.$refs.locationMap.remakeMarkers()
        },
        searchFilters: {
            handler() {
                this.getMtds({
                    search: this.searchFilters.search_text,
                    rsm_id: this.searchFilters.rsm_id,
                    tag_ids: this.searchFilters.tags,
                    exclude_tag_ids: this.searchFilters.exclude_tags,
                    last_order_date: this.searchFilters.not_ordered_since === 'Never' ? 'never' : moment(this.searchFilters.not_ordered_since).toISOString(true),
                    last_activity_at: this.searchFilters.not_active_since === 'Never' ? 'never' : moment(this.searchFilters.not_active_since).toISOString(true),
                })
            },
            deep: true,
        }
    },
    computed: {
        usersPerPage() {
            return parseInt(this.users?.per_page || 0)
        },
        textMedia() {
            if (this.formText.media) {
                return URL.createObjectURL(this.formText.media)
            }
            return '#'
        },
        syncProductsStatusText() {
            return this.syncProductsStatus ? `Last run at ${moment.utc(this.syncProductsStatus.created_at).format('M/D/YY H:m:s')} (${this.syncProductsStatus.status})` : ''
        },
        selectedUserIds() {
            return this.selectedUsers.map((user) => {
                return user.id;
            });
        }
    },
    methods: {
        searchMtds(e) {
            this.mtds = this.mtdsLight.filter(user => user.toLowerCase().includes(e.query.toLowerCase()))
        },
        updateSearchText(selectedObject) {
            this.searchFilters.search_text = selectedObject ? selectedObject.name : '';
        },
        getMtds(query) {
            this.$inertia.form({}).post(this.route('mtd', { page: 1, per_page: this.users.per_page, ...query }), {
                preserveState: true,
                preserveScroll: true,
                resetOnSuccess: true,
                only: ['users'],
            });
        },
        onTableStateRestore(e) {
            this.getMtds({ page: e.first / e.rows + 1, per_page: e.rows })
        },
        onPage(e) {
            this.getMtds({ page: e.page + 1, per_page: e.rows })
        },
        onSort(e) {
            this.getMtds({ sort_field: e.sortField, sort_order: e.sortOrder })
        },
        getHistories() {
            this.$inertia.reload({
                only: ['textHistories'],
            })
        },
        showErrors(textSendStatusError) {
            this.textSendStatusError = textSendStatusError
            $('#textStatusErrorModal').modal('show');
        },
        clearSearch() {
            this.searchFilters.reset();
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
        sendText() {
            if (this.selectedUserIds.length === 0) return;

            this.formText.selected = this.selectedUserIds;
            this.formText.post(this.route('mtd.sendtext'))
        },
        sendTestText() {
            this.formText.post(this.route('mtd.sendtesttext'))
        },
        runSyncProducts(e) {
            this._syncProductsIsRunning = true
            axios.post(this.route('mtd.syncProducts'))
        },
        getBitly() {
            axios.post(this.route('admin.bitly'), {
                url: this.url
            }).then((response) => {
                this.bitly = response.data.url;
            });
        },
        setDateByPeriod(field, period) {
            let date = 'Never';

            if (period !== 'never') {
                date = moment().subtract(1, period).startOf('day').toDate();
            }

            this.searchFilters[field] = date;
        },
        onSelectionChange(data) {
            this.selectedUsers = data;
        },
    },
}
</script>

<style src="@vueform/multiselect/themes/default.css"></style>

<style>
.p-autocomplete input {
    width: 100%;
    height: calc(1.6em + 0.75rem + 2px);
    padding: 0.375rem 0.75rem;
    font-size: 0.9rem;
}
</style>
